import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import {SHIPPING_ADDRESS_TRANSLATIONS} from './shipping-address-translations.js';
import template from './ns-shipping-address.html';
import { SHIPPING_ADDRESS_CONFIG } from './shipping-address-config.js';
import { FormMixin } from '../formMixin.js';

/**
 * Basic shipping address form, has fields for address line1 and address line 2, name,
 * email, instructions, city, state, zip/postal code also contains checkboxes for
 * default shipping method and save shipping method.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Shipping Address Component</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-shipping-address></ns-shipping-address>
 *   </template>
 * </demo-snippet>
 */
export class NsShippingAddress extends FormMixin(PolymerElement) {
    static get is() {
        return 'ns-shipping-address';
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * country
             * Country Code Property. Set in ready(), defaults to 'US'.
             * @example 'DE'
             */
            country: {
                type: String,
                value: "US",
                observer: '_initialize'
            },
            /**
             * language
             * Language Code Property. Set in ready(), defaults to 'en'.
             * @example 'jp'
             */
            language: {
                type: String,
                value: "en",
                observer: '_initialize'
            },
            /**
             * If we have a countryList then the user can pick a country to ship to
             */
            countryList: {
                type: Array
            },
            /**
             * Used to validate the inputs value against.
             */
             shippingAddressNameRegex: {
                type: String,
                value: "^(?=.*[a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFF. !@#&amp;*-\u0080-\u052F])[a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFFs. !@#&amp;*',-\u0080-\u052F]*$"
            },
            /**
             * shippingAddressName
             * Property value for field, available for two-way binding.
             * @example 'John Doe'
             */
            shippingAddressName: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * shippingAddress1
             * Property value for field, available for two-way binding.
             * @example '2247 Sunny Hills Road'
             */
            shippingAddress1: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * shippingAddress2
             * Property value for field, available for two-way binding.
             * @example 'Apt. #324'
             */
            shippingAddress2: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * shippingCity
             * Property value for field, available for two-way binding.
             * @example 'Provo'
             */
            shippingCity: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * shippingState
             * Property value for field, available for two-way binding.
             * @example 'DE'
             */
            shippingState: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * shippingPostalCode
             * Property value for field, available for two-way binding.
             * @example '44331'
             */
            shippingPostalCode: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * shippingCountry
             * Property value for field, available for two-way binding
             */
            shippingCountry: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Label for country select
             */
            shippingCountryDropdownLabel: {
                type: String,
                value: 'Country',
            },
            /**
             * Index of selected shippingCountry
             */
            shippingCountryIdx: {
                type: Number,
                observer: '_onCountryIdxChanged'
            },
            /**
             * shippingPhone
             * Property value for field, available for two-way binding.
             * @example '555-555-5555'
             */
            shippingPhone: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * shippingPhoneCountry
             * Property value for field, available for two-way binding.
             * @example 'GB'
             */
            shippingPhoneCountry: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * shippingMobilePhone
             * Property value for field, available for two-way binding.
             * @example '555-555-5555'
             */
            shippingMobilePhone: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * shippingMobilePhoneCountry
             * Property value for field, available for two-way binding.
             * @example 'FR'
             */
            shippingMobilePhoneCountry: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * shippingEmail
             * Property value for field, available for two-way binding.
             * @example 'noreply@gmail.com'
             */
            shippingEmail: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * requireEmail
             * sets email field as required
             */
            requireEmail: {
                type: Boolean,
                value: false
            },
            /**
             * requireShippingInstructions
             * sets shipping instructions field as required
             */
            requireShippingInstructions: {
                type: Boolean,
                value: false
            },
            /**
             * shippingInstructions
             * Property value for field, available for two-way binding.
             * @example 'Place shipment behind chair'
             */
            shippingInstructions: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * isDefaultShipping
             * Flag for defaultShipping checkbox, available for two-way binding. Default value is false.
             * Sends on-change event when value changes in checkbox.
             * @example false
             */
            isDefaultShipping: {
                type: Boolean,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Flag indicating whether this address should be saved to the user's profile
             */
            shouldSaveAddress: {
                type: Boolean,
                notify: true
            },
            /**
             * Flag indicating whether this address is already saved to the user's profile
             */
            isSavedAddress: {
                type: Boolean,
                observer: '_onChangeIsSavedAddress'
            },
            /**
             * valid
             * Flag to determine if value is valid, passed to ns-input.
             * @example true
             */
            valid: {
                type: Boolean,
                notify: true
            },
            /**
             * hideDefaultCheckbox
             * Flag to determine if component displays default checkbox
             */
            hideDefaultCheckbox: {
                type: Boolean,
                value: false
            },
            /**
             * hideSaveCheckbox
             * Flag to determine if component displays save checkbox
             */
            hideSaveCheckbox: {
                type: Boolean,
                value: false
            },
            /**
             * showEmailField
             * Flag to determine if component displays the email field
             */
            hideEmailField: {
                type: Boolean,
                value: false
            },
            /**
             * showShippingInstructions
             * Flag to determine if component displays the shipping instructions field
             */
            hideShippingInstructions: {
                type: Boolean,
                value: false
            },
            /**
             * Flag to determine if component should display the mobile phone field.
             */
            showMobilePhoneField: {
                type: Boolean,
                value: false
            },
            /**
             * Shipping County
             */
            shippingCounty: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Translations from the server. Triggers the translation assignment process.
             * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
             */
            serverTranslations: {
                type: Object,
                observer: '',
                value: function() {
                    return {}
                }
            },
            useServerTranslations: {
                type: Boolean,
                value: false
            },
            /**
             * dirty flag for the form
             */
            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * flag for whether or not to sort state list
             */
            sortStateList: {
                type: Boolean,
                value: false
            },
            /**
             * Makes the recipient field not editable except by Brand Affiliates.
             */
            recipientFieldEditableOnlyByBrandAffiliates: {
                type: Boolean,
                value: false
            },
            shippingAddressNameDisabled: {
                type: Boolean,
                value: false,
                computed: 'computeShippingAddressNameDisabled(recipientFieldEditableOnlyByBrandAffiliates)'
            },
            fieldType: {
                type: String,
                value: ""
            }
        };
    }

    static get observers() {
        return [
            '_updateDirtyFlag(nameDirty, addressDirty, phoneDirty, emailDirty, shippingInstructionsDirty, defaultShippingDirty, savedShippingDirty)',
            '_assignTranslations(serverTranslations, useServerTranslations)'
        ];
    }

    /**
     * Polymer life cycle event
     * In this case its used to pull all translations for the page to display
     */
    ready() {
        super.ready();
        this._initialize();
    }

    _initialize() {
        if (this.country && this.country.length === 2 && this.language && this.language.length === 2) {
            if (!this.attributes.hasOwnProperty("shipping-country")) {
                this.shippingCountry = this.country;
            }
            this._assignTranslations()
            this._setConfig(this.country, SHIPPING_ADDRESS_CONFIG);
            // If a country list is set then they can choose a country other than their current country to ship to
            if (!this.countryList) {
                this.shippingCountry = this.country;
            } else {
                // Setting starting shipping country on the dropdown
                this.countryList.forEach((item, i) => {
                    if (item.value === this.shippingCountry) this.shippingCountryIdx = i;
                });
            }
        }
    }

    /**
     * Observer for the property "isSavedAddress"
     * @param {boolean} isSaved
     */
    _onChangeIsSavedAddress(isSaved) {
        // Address is already saved; set "save to my account" checkbox to checked state
        if (isSaved) {
            this.set('shouldSaveAddress', true);
        }
    }

    /**
     * Observer for the property "shippingCountryIdx" to also set shippingCountry
     * @param {number} index
     */
    _onCountryIdxChanged(index) {
        if (index > -1) {
            this.shippingCountry = this.countryList[index].value;
        }
    }

    computeShippingAddressNameDisabled(recipientFieldEditableOnlyByBrandAffiliates) {
        if (
            recipientFieldEditableOnlyByBrandAffiliates
            && nuskin
            && nuskin.account
            && nuskin.account.AccountManager
            && nuskin.account.AccountManager.currentData
            // Account type 10 is a BA.
            && nuskin.account.AccountManager.currentData.accountType !== '10'
        ) {
            return true
        }
        return false;
    }

    /**
     * Observer on serverTranslations that gathers all translations and maps them to properties.
     * Any strings set as properties and defined in the html attributes take precedence over translations from the server,
     * which in turn take precedence over the default translations in the translations file.
     */
    async _assignTranslations () {
        this._localize(this.language, this.country, SHIPPING_ADDRESS_TRANSLATIONS, this.serverTranslations, this.useServerTranslations);
    }

    /**
     * Form validation - ns-form looks for this function for validation
     */
    validate() {
        return this.shadowRoot.querySelector('ns-form').validate();
    }

    /**
     * This function will reset the form show we don't show error messages
     */
    reset() {
        return this.shadowRoot.querySelector('ns-form').reset();
    }

    /**
     * This function will return true if the form has been modified
     */
    dirty() {

    }
}

customElements.define(NsShippingAddress.is, NsShippingAddress);
